
/**
 * object validator for Rocketship apps
 *
 * @module rocketship-validator
 */

/*
    Module to provide input validation
    for form submissions, ReST APIs, etc.
*/

// Dependencies
const
    differenceInYears = require('date-fns/differenceInYears');

const SIMPLE_NUMERIC_ZIP = {
    IT: 5,
    DE: 5,
    ES: 5,
    FR: 5,
    DK: 4,
    AU: 4,
    NZ: 4,
};

const coreGuards = {
    required: function required (value, key, obj, opts) {
        const validation = this,
            filled = isFilled(value);
        if (!filled) validation.addError(key, 'NULL');
        return filled;
    },
    requiresField: function requiresField (value, key, obj, opts) {
        const validation = this;

        if (!opts[0]) throw new Error('requiresField guard needs field to require');

        if (isFilled(value)) {
            const other = String(opts[0]),
                otherFilled = isFilled(obj[other]);
            if (!otherFilled) validation.addError(other, 'NULL', 'required by ' + key);
            return otherFilled;
        }

        return true;
    },
    requiresFieldGroup: function requiresFieldGroup (value, key, obj, opts) {
        return opts.every(function (other) {
            return coreGuards.requiresField.call(this, value, key, obj, [other]);
        }, this);
    },
    matchesField: function matchesField (value, key, obj, opts) {

        if (!opts[0]) throw new Error('matchesField guard needs field to match');

        if (isFilled(value)) {
            return value === obj[opts[0]];
        }

        return true;
    },
    minAge: function minAge (value, key, obj, opts) {
        opts = opts || [];
        const
            validation = this,
            minimumAge = opts[1] || 18;
        let missingField = false,
            currentTime = opts[0];

        // If age is a checkbox.
        if (coreGuards.isChecked.test(value)) {
            value = minimumAge;
        }
        // If age is a dropdown.
        else if (isFilled(value)) {
            value = parseInt(value, 10);
        }
        else {
            // If there's no currentTime, we can't validate birth date fields
            // so return a generic null field error.
            if (!currentTime) {
                if (validation.addError) validation.addError(key, 'NULL');
                return false;
            }
            // Support moment/dayjs/etcetera.
            if (typeof currentTime.toDate === 'function') {
                currentTime = currentTime.toDate();
            }

            ['month', 'day', 'year'].forEach((field) => {
                const key = 'age.birth_' + field;
                if (!isFilled(obj[key])) {
                    if (validation.addError) validation.addError(key, 'NULL');
                    missingField = true;
                }
            });

            if (missingField) {
                return false;
            }

            const
                year = Number(obj['age.birth_year']),
                // translate month number to month index
                monthIndex = Number(obj['age.birth_month'] - 1),
                day = Number(obj['age.birth_day']);

            const birthDate = new Date(year, monthIndex, day);

            // Check if birthDate was nominally valid.
            if (isNaN(birthDate.getTime())) {
                return false;
            }
            // JS `Date` does some "helpful" things with strange inputs, like
            // rolling over to the next month if the number of days is too large.
            // Here we'll flag the field as invalid if JS made any changes from
            // the actual input.
            if (
                birthDate.getFullYear() !== year ||
                birthDate.getMonth() !== monthIndex ||
                birthDate.getDate() !== day
            ) {
                return false;
            }

            // User is eligible if they are the minimum age as of the currentTime.
            value = differenceInYears(currentTime, birthDate);
        }

        return value >= minimumAge;
    },
    isIn: function isIn (value, key, obj, opts) {
        return ~opts.indexOf(value);
    },
    isPhone: function isPhone (value, key, obj) {
        const { country } = obj;
        let phone = value;

        if (!isFilled(phone)) return true;

        // From Toy::Validator::phone
        if (country) {
            phone = phone.replace(/[()\s.-]+/g, '');

            // Wikipedia is the source for these phone number formats.
            // Especially: http://en.wikipedia.org/wiki/Telephone_numbers_in_Europe
            // The phone numbers are validated very conservatively--there's no
            // guarantee that the zone prefix is valid for example.

            if (country === 'US' || country === 'CA') { // +1
                return (/^[2-9]\d{2}[2-9]\d{6}$/).test(phone);
            }
            else if (country === 'UK' || country === 'GB') { // +44
                return (/^\d{9,11}$/).test(phone);
            }
            else if (country === 'DE') { // +49
                return (/^\d{1,12}$/).test(phone);
            }
            else if (country === 'NL') { // +31
                return (/^\d{10}$/).test(phone);
            }
            else if (country === 'DK') { // +45
                return (/^\d{8}$/).test(phone);
            }
            else if (country === 'ES') { // +34
                return (/^\d{9}$/).test(phone);
            }
            else if (country === 'IT') { // +39
                return (/^\d{8,11}$/).test(phone);
            }
            else if (country === 'FR') { // +33
                return (/^\d{9}$/).test(phone);
            }
            else if (country === 'FI') { // +358
                return (/^\d{5,12}$/).test(phone);
            }
            else if (country === 'AU') { // +61
                // (0x) xxxx xxxx - http://en.wikipedia.org/wiki/Telephone_numbers_in_Australia
                return (/^0\d{9}$/).test(phone);
            }
            else if (country === 'NZ') { // +64
                // (0x) xxx xxxx or (0xx) xxx xxxx
                // http://en.wikipedia.org/wiki/Telephone_numbers_in_New_Zealand
                return (/^0\d{8,9}$/).test(phone);
            }
        }
        else {
            // eslint-disable-next-line no-console
            console.warn('isPhone validator failing open because no country was provided');
        }

        return (/\d/).test(phone);
    },
    isZip: function isZip (value, key, obj) {
        const { country } = obj,
            zip = value;

        if (!isFilled(zip)) return true;

        if (country) {
            if (country === 'US') {
                // '11111' or '11111-1111'
                return (/^\d{5}(?:-\d{4})?$/).test(zip);
            }
            else if (country === 'CA') {
                // 'a1a1a1' or 'a1a 1a1' or 'a1a-1a1'
                return (/^[a-z]\d[a-z](?:\s*|-)\d[a-z]\d$/i).test(zip);
            }
            else if (country === 'UK' || country === 'GB') {
                // http://www.upu.int/post_code/en/countries/GBR.pdf
                // basically mix of chars and digits, but can vary to 6 diff formats
                return (/^[a-z]{1,2}\d[\da-z]?\s*\d[a-z]{2}$/i).test(zip) ||
                    (/^GIR\s*0AA$/i).test(zip);
            }
            else if (country === 'FI') {
                // http://www.upu.int/post_code/en/countries/FIN.pdf
                // '11111' or 'FI-11111'
                return (/^(FI-)?\d{5}?$/).test(zip);
            }
            else if (country === 'NL') {
                // http://en.wikipedia.org/wiki/Postal_codes_in_the_Netherlands
                // AA 1111
                return (/^\d{4}\s?[a-z]{2}$/i).test(zip);
            }
            else if (SIMPLE_NUMERIC_ZIP[country]) {
                const len = SIMPLE_NUMERIC_ZIP[country];
                return !!(((/^\d+$/).test(zip) && (len === '*' || String(zip).length == len)));
            }
        }
        else {
            // eslint-disable-next-line no-console
            console.warn('isZip validator failing open because no country was provided');
        }

        return true;
    },
    isChecked: /^(?:yes|1|true)$/i,
    // For use with guards from `validator`, like `external: [isEmail]`.
    external: function callExternalGuard (value, key, obj, [guard, ...opts]) {
        if (!(typeof guard === 'function')) throw new Error('external guards must be functions');
        // If the value is blank, the "required"
        // validator should be allowed to handle it.
        if (!isFilled(value)) return true;
        // The validator module only works on strings, so we stringify the value here.
        const isValid = guard(String(value), ...opts);
        if (!isValid && guard.name) {
            this.addError(key, 'VALIDATION', 'failed ' + guard.name + ' test');
        }
        return isValid;
    },
};

function isFilled (value) {
    if (value === null || value === undefined) return false;
    if (typeof value === 'object' && !Object.keys(value).length) return false;
    const str = String(value);
    if (!str || str.match(/^\s*$/)) return false;
    return true;
}

function Validator (guardsToPrebuild) {
    if (!(this instanceof Validator)) return new Validator(guardsToPrebuild);

    let preBuiltGuards;

    // Pre-build guards passed in if the parameter
    // is in an acceptable format.
    if (guardsObjectLooksGood(guardsToPrebuild)) {
        preBuiltGuards = {};

        Object.keys(guardsToPrebuild).forEach((key) => {
            preBuiltGuards[key] = buildGuard(guardsToPrebuild[key], key);
        });
    }

    async function validate (obj, guards) {
        // TODO: do we really need to support promises for these args?
        [obj, guards] = await Promise.all([obj, guards]);

        const validation = new Validation(obj),
            builtGuards = {};

        // Return early if this is not an object we're equipped to validate.
        if (!obj) {
            throw new ValidationError('validate() requires object', validation);
        }

        if (guards) {
            Object.keys(guards).forEach((key) => {
                builtGuards[key] = buildGuard(guards[key], key);
            });
        }

        // TODO: support specifying a guard in both
        // prebuilt and dynamic.
        if (preBuiltGuards) {
            Object.keys(preBuiltGuards).forEach((key) => {
                if (builtGuards[key]) return;

                builtGuards[key] = preBuiltGuards[key];
            });
        }

        // Reject if no guards were properly specified.
        if (!guardsObjectLooksGood(builtGuards)) {
            throw new Error('no guards specified');
        }

        // Validate each key according to its specified guard.
        // Keys not specified in the guards objects are ignored.
        const fieldPromises = Object.keys(builtGuards).map((key) =>
            validation._callBuiltGuard(builtGuards[key], obj[key], key, obj));

        await Promise.all(fieldPromises);

        const numErrors = validation.getAllErrors().length;
        let errorKeys;

        if (numErrors > 0) {
            errorKeys = Object.keys(validation.getErrors()).join(', ');

            const msg = numErrors + ' error' + (numErrors === 1 ? '' : 's') +
                ' found in ' + errorKeys;

            throw new ValidationError(
                msg,
                validation,
            );
        }

        return obj;
    }

    function guardsObjectLooksGood (guardsObj) {
        return guardsObj && !!Object.keys(guardsObj).length;
    }

    // Returns a function that may be called to determine
    // the validity of the given field based on the given guard.
    // Supports any of the built-in guards as strings,
    // custom regex matches, or entirely custom function guards.
    // Built guards are functions that return true if the field is valid.
    function buildGuard (guard, key) {
        let builtGuard,
            guardName,
            subGuards;

        if (typeof guard == 'string') {
            guardName = guard;
            guard = findBuiltInGuard(guard);
        }

        if (typeof guard === 'object' && !Array.isArray(guard) && !(guard instanceof RegExp)) {
            if (!Object.keys(guard).length) {
                builtGuard = buildEmptyGuard();
            }
            else {
                subGuards = Object.keys(guard).map((subGuardName) =>
                    buildSubGuard(subGuardName, guard[subGuardName], key));
                builtGuard = accumulateSubGuards(subGuards, key);
            }
        }
        else {
            builtGuard = buildSingleGuard(guardName, guard);
        }

        if (!builtGuard) throw new Error('failed to build guard for ' + key);

        return builtGuard;
    }

    function buildSingleGuard (guardName, guard, guardOpts) {
        let builtGuard;

        switch (typeof guard) {
            case 'function':
                // Pass through
                builtGuard = guard;
                break;
            case 'object':
                if (Array.isArray(guard)) {
                    // Pass through
                    builtGuard = guard;
                }
                else if (guard instanceof RegExp) {
                    builtGuard = buildMatcherGuard(guardName, guard);
                }
                else {
                    throw new Error('unrecognized guard object: ' + JSON.stringify(guard, null, 2));
                }
                break;
            default:
                throw new Error('unrecognized guard: ' + guardName + ': ' + typeof guard);
        }

        return guardOpts ? [builtGuard, guardOpts] : builtGuard;
    }

    function findBuiltInGuard (guardName) {
        return coreGuards[guardName];
    }

    function buildMatcherGuard (guardName, regex) {
        /* jslint evil:true */
        // Use eval to set name of function. Kinda dumb.
        const
            fnName = guardName.replace(/^[^$A-Z_]+/i, '').replace(/[^0-9A-Z_$]+/gi, ''),
            matcherFn = eval(
                'function ' + fnName + '(value, key, obj, opts) {' +
            '/* If the value is blank, the "required"' +
            '   validator should be allowed to handle it. */' +
            'if (!isFilled(value)) return true;' +
            'return regex.test(value);' +
            '};' + fnName,
            );
        return matcherFn;
    }

    function buildSubGuard (subGuardName, subGuardValue, key) {
        if (subGuardValue === false) {
            return buildEmptyGuard();
        }

        const foundBuiltInGuard = findBuiltInGuard(subGuardName);

        if (foundBuiltInGuard) { // If the name is a built-in, then the value is the opts.
            return buildSingleGuard(
                subGuardName,
                foundBuiltInGuard,
                // Only pass along actual opts, not the default "true".
                subGuardValue !== true ? subGuardValue : undefined,
            );
        }
        else { // Else the value is the actual guard.
            return buildSingleGuard(subGuardName, subGuardValue);
        }
    }

    // Returns a guard that always says its field is valid.
    function buildEmptyGuard () {
        return function emptyGuard (key, value, obj, opts) {
            return true;
        };
    }

    // Return a function that accumulates the results
    // of several guard functions for one field.
    function accumulateSubGuards (subGuards, key) {
        return async function testSubGuards (value, key, obj, opts) {
            this._waiveDefaultError();

            const fieldPromises = subGuards.map(
                (builtSubGuard) =>
                    this._callBuiltGuard(builtSubGuard, obj[key], key, obj),
            );

            const fields = await Promise.all(fieldPromises);

            // find() will return the value (false) if it can find it,
            // otherwise it returns undefined, which !== false.
            const subGuardIsValid = fields.find((fieldIsValid) => fieldIsValid === false) !== false;
            return subGuardIsValid;
        };
    }

    this.validate = validate;
}

// The context of a single validation.
// Kept separate from the Validator so that a single validator
// can run several validations at once without conflict.
function Validation (objToValidate) {
    if (!(this instanceof Validation)) return new Validation(objToValidate);

    this._errorsByField = {};
    this._allErrors = [];

    // Just a convenience ref
    this.obj = objToValidate;
}

Validation.prototype._callBuiltGuard =
async function _callBuiltGuard (builtGuard, value, key, obj) {
    const self = this;
    let opts = [],
        guardName,
        guard,
        addDefaultError;

    if (Array.isArray(builtGuard)) {
        // Guard with opts
        guardName = builtGuard[0].name;
        guard     = builtGuard[0];
        opts      = opts.concat(builtGuard[1]);
    }
    else {
        guardName = builtGuard.name;
        guard     = builtGuard;
    }

    // Call the guard function with `this` set to a
    // reference to the Validation instance.
    guard = guard.bind({
        ...Validation.prototype,
        ...self,
        addError: function guardAddError () {
            // Set this flag so we can detect if a guard
            // function already handled its own error message.
            addDefaultError = false;

            return self.addError.apply(self, arguments);
        },
        // For use by guards whose only purpose is to
        // test other guards, e.g. `testSubGuards()`.
        _waiveDefaultError: function _guardWaiveDefaultError () {
            addDefaultError = false;
        },
    });

    addDefaultError = true;

    const isValid = await guard(value, key, this.obj, opts);

    if (!isValid && addDefaultError) {
        self.addError(key, 'VALIDATION', 'failed ' + (guardName || 'guard') + ' test');
    }

    return isValid;
};

Validation.prototype.addError = function addError (name, error, optionalMessage) {
    const errorObj = {
        name,
        error,
        message: (optionalMessage || ''),
    };

    if (name && !this._errorsByField[name]) this._errorsByField[name] = errorObj;
    this._allErrors.push(errorObj);

    return this;
};

// Retrieve a full list of the set of errors
// that were accumulated during `validate()`.
Validation.prototype.getAllErrors = function getAllErrors () {
    return this._allErrors;
};

// Retrieve an obj of the first error
// that was set for each field during `validate()`.
Validation.prototype.getErrors = function getErrors () {
    return this._errorsByField;
};

class ValidationError extends Error {
    constructor (message, validation) {
        super(message);

        this.name = this.constructor.name;
        this.message = message || '';
        this.validation = validation;
        this.status = 400;

        // captureStackTrace is a node.js-only method.
        // BUT it's implemented all over. Except for Safari iOS
        if (typeof Error.captureStackTrace === 'function') {
            Error.captureStackTrace(this, this.constructor);
        }
    }

    toJSON () {
        return { errors: this.validation && this.validation.getAllErrors() };
    }
}

Validator.Validation = Validation;
Validator.ValidationError = ValidationError;
Validator.isFilled = isFilled;

// Alias to `validators` for backwards compatibility.
Validator.prototype.coreGuards = Validator.prototype.validators = coreGuards;

module.exports = Validator;
